import { SyntheticEvent, useState } from "react";
import { Divider, Stack, Tab, Tabs, Typography, Button } from "@mui/material";
import { useTranslation } from "react-i18next";
import CircledBackButton from "@/features/Common/components/CircledBackButton";
import Panel from "@/features/Common/components/Panel";
import theme, { defaultFullscreenPageStyling } from "@/theme";
import { useNavigate } from "react-router";
import { trackEvent, trackPageViewV2 } from "@/services/analytics-adapter";
import { formatDateToReadableString } from "@/utils";
import useGetMemberVouchers from "@/features/Vouchering/hooks/useGetMyVouchers";
import IconLoader from "@/features/Common/components/IconLoader";
import { VoucherOrderStatus } from "@/services/core-api-adapter";

export default function MyVouchers() {
  const { t } = useTranslation();

  const navigate = useNavigate();

  const myVoucherRoutes = ["added", "used", "expired"];

  const [selectedTab, setSelectedTab] = useState<string>(myVoucherRoutes[0]);

  function filterVouchersByStatus(voucherStatus: VoucherOrderStatus) {
    return memberVouchers.memberVouchers?.vouchers.filter(
      (x) => x.voucherStatus === voucherStatus
    );
  }

  const memberVouchers = useGetMemberVouchers();

  const addedVouchers = filterVouchersByStatus(VoucherOrderStatus.ASSIGNED);
  const usedVouchers = filterVouchersByStatus(VoucherOrderStatus.REDEEMED);
  const expiredVouchers = filterVouchersByStatus(VoucherOrderStatus.EXPIRED);

  function onTabChange(_event: SyntheticEvent, newTab: string) {
    const transformedTabName = newTab
      .split("-")
      .map((word, index) => {
        if (index === 0) {
          return word;
        }
        return word.charAt(0).toUpperCase() + word.slice(1);
      })
      .join(" ");

    trackEvent({
      event: `action.myVouchersTabClicked`,
      "myVouchersTabClicked.name": transformedTabName,
      source: "My vouchers",
    });

    trackPageViewV2({
      pageName: `My vouchers:${transformedTabName}`,
      pageSubSection1: "My vouchers",
      pageSubSection2: `My vouchers:${transformedTabName}`,
      pageCategory: "My vouchers",
    });

    setSelectedTab(newTab);
  }

  function renderVoucher(
    name: string,
    description: string,
    expiryDate: Date,
    assignedOnDate: Date,
    voucherStatus: string,
    voucherCode: string,
    index: number
  ) {
    return (
      <Panel key={index}>
        <Stack spacing={1}>
          <Typography variant="h4" color={theme.palette.neutral[500]}>
            {t("More.MyVouchers.Added.subtitle", {
              date: formatDateToReadableString(assignedOnDate),
            })}
          </Typography>
          <Typography variant="h2">
            {t("More.MyVouchers.Added.card.title")}
          </Typography>
          <Typography lineHeight={1}>{voucherCode}</Typography>
          <Typography variant="h2" pt={2}>
            {t("More.MyVouchers.Added.card.Services.title")}
          </Typography>
          <Typography
            variant="h4"
            lineHeight={1}
            color={theme.palette.neutral[500]}
          >
            {name}
          </Typography>
          <Typography
            variant="h5"
            lineHeight={1}
            color={theme.palette.neutral[500]}
          >
            {description}
          </Typography>

          {voucherStatus === "ASSIGNED" && (
            <>
              <Typography color={theme.palette.neutral[500]} pt={2}>
                {t("More.MyVouchers.Added.card.footerText", {
                  date: formatDateToReadableString(expiryDate),
                })}
              </Typography>
              <Divider />
            </>
          )}

          {voucherStatus === "EXPIRED" && (
            <>
              <Typography color={theme.palette.neutral[500]} pt={2}>
                {t("More.MyVouchers.Expired.card.footerText", {
                  date: formatDateToReadableString(expiryDate),
                })}
              </Typography>
            </>
          )}

          {voucherStatus === "REDEEMED" && (
            <>
              <Typography color={theme.palette.neutral[500]} pt={2}>
                {t("More.MyVouchers.Used.card.footerText", {
                  date: formatDateToReadableString(expiryDate),
                })}
              </Typography>
            </>
          )}

          {voucherStatus === "ASSIGNED" && (
            <Stack direction="row" justifyContent="space-between">
              <Button>{t("More.MyVouchers.Added.card.buttonText")}</Button>
            </Stack>
          )}
        </Stack>
      </Panel>
    );
  }

  return (
    <Stack spacing={3} sx={{ ...defaultFullscreenPageStyling }}>
      <CircledBackButton
        showLabel={true}
        onClick={() => navigate("/home/more")}
      />
      <Stack my={3}>
        <Typography variant="h2">{t("More.MyVouchers.title")}</Typography>
      </Stack>
      <Tabs
        value={selectedTab}
        onChange={onTabChange}
        variant="scrollable"
        scrollButtons={false}
        aria-label={t("MyHealth.tabNavigation.label")}
        textColor="primary"
        sx={{
          mx: defaultFullscreenPageStyling.px * -1,
          "& .MuiTabs-flexContainer::after": {
            content: '" "',
            minWidth: (theme) => theme.spacing(defaultFullscreenPageStyling.px),
            position: "relative",
          },
        }}
      >
        <Tab
          value={myVoucherRoutes[0]}
          label={"Added" + " (" + addedVouchers?.length + ")"}
          sx={{
            ml: defaultFullscreenPageStyling.px,
          }}
        />
        <Tab
          value={myVoucherRoutes[1]}
          label={"Used" + " (" + usedVouchers?.length + ")"}
        />
        <Tab
          value={myVoucherRoutes[2]}
          label={"Expired" + " (" + expiredVouchers?.length + ")"}
        />
      </Tabs>

      {selectedTab === "added" && addedVouchers && addedVouchers?.length > 0 ? (
        addedVouchers?.map((item, index) =>
          renderVoucher(
            item.name,
            item.description,
            new Date(item.expiryDate),
            new Date(item.assignedOn),
            item.voucherStatus,
            item.voucherCode,
            index
          )
        )
      ) : selectedTab === "used" && usedVouchers && usedVouchers?.length > 0 ? (
        usedVouchers?.map((item, index) =>
          renderVoucher(
            item.name,
            item.description,
            new Date(item.expiryDate),
            new Date(item.assignedOn),
            item.voucherStatus,
            item.voucherCode,
            index
          )
        )
      ) : selectedTab === "expired" &&
        expiredVouchers &&
        expiredVouchers?.length > 0 ? (
        expiredVouchers?.map((item, index) =>
          renderVoucher(
            item.name,
            item.description,
            new Date(item.expiryDate),
            new Date(item.assignedOn),
            item.voucherStatus,
            item.voucherCode,
            index
          )
        )
      ) : (
        <Stack>
          <IconLoader
            icon="TrolleyWithPlusIcon"
            sx={{ fontSize: 250, paddingLeft: 10 }}
          />
          <Stack>
            <Typography
              variant="h3"
              color={theme.palette.neutral[500]}
              paddingLeft={2}
            >
              You currently have no {selectedTab} vouchers
            </Typography>
          </Stack>
        </Stack>
      )}
    </Stack>
  );
}
